/*
 * 08 October 2026 - SKU 'CONTAINS' MATCHING ON THE FULL RESULTS PAGE
 *
 * Shopify's search only matches the START of a word or SKU, so a fragment from the
 * middle of a SKU (e.g. msh240 inside RAPIDMSH240V) finds nothing. The search
 * dropdown already handles this (see assets/predictive-search.js); this file does
 * the same for the full results page that 'View all' and the Enter key go to.
 *
 * How it works:
 *  1. sections/search-results.liquid outputs a hidden marker element
 *     (data-mr-sku-results) - but only on page 1 with the default sort, so a
 *     customer's own sort or later pages are never altered.
 *  2. This script reads the search text from the marker and matches it against the
 *     SKU list (the same list the dropdown uses: downloaded from the mr-sku-index
 *     section and kept in localStorage for 24 hours, key mr_sku_index_v1).
 *  3. It fetches the product cards for the matching products from the results
 *     section itself (search?q=id:1 OR id:2&section_id=...), so they look exactly
 *     like every other card, and puts them at the top of the grid. Any of those
 *     products that Shopify also returned lower down are removed so nothing repeats.
 *  4. If Shopify found no results at all (so the page is showing its fallback
 *     products), the fallback products are replaced by the SKU matches.
 *
 * It fails safe: if the SKU list cannot be loaded or anything here throws, the page
 * is left exactly as Shopify rendered it. Sorting or filtering re-draws the grid
 * from Shopify's own results, which drops the SKU matches - that is intended.
 *
 * KEEP IN STEP: the helper functions below (normaliseSku to findSkuMatchProductIds)
 * are a copy of the ones in assets/predictive-search.js. If the matching rules
 * change in one, change them in the other.
 */
const SKU_INDEX_SECTION_ID = 'mr-sku-index';
const SKU_INDEX_STORAGE_KEY = 'mr_sku_index_v1';
const SKU_INDEX_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const SKU_INDEX_PARALLEL_REQUESTS = 4;
const SKU_MIN_TERM_LENGTH = 3;
const SKU_MIN_CONTAINS_LENGTH = 4;
const SKU_MAX_RESULTS = 12;

/**
 * @typedef {object} SkuRow
 * @property {string} normalised - The SKU in lower case with only letters and numbers
 * @property {number} productId - The id of the product the SKU belongs to
 */

/** @type {Promise<SkuRow[]> | null} */
let skuRowsPromise = null;

/**
 * Lower-cases a SKU or search term and strips everything except letters and numbers.
 * @param {unknown} value - The SKU or term
 * @returns {string} The normalised text
 */
function normaliseSku(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Reads the stored SKU list, if there is one and it is still fresh.
 * @returns {Array<[string, number]> | null} The stored rows
 */
function readStoredSkuIndex() {
  try {
    const raw = window.localStorage.getItem(SKU_INDEX_STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw);
    if (!stored || !Array.isArray(stored.rows) || typeof stored.savedAt !== 'number') return null;
    if (Date.now() - stored.savedAt > SKU_INDEX_MAX_AGE_MS) return null;

    return stored.rows;
  } catch (error) {
    return null;
  }
}

/**
 * Stores the SKU list in the browser. Ignored if storage is full or blocked.
 * @param {Array<[string, number]>} rows - The rows to store
 */
function storeSkuIndex(rows) {
  try {
    window.localStorage.setItem(SKU_INDEX_STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), rows }));
  } catch (error) {
    // Storage is full or blocked - the list is simply downloaded again next time.
  }
}

/**
 * Downloads one page of the SKU list.
 * @param {number} page - The page number, starting at 1
 * @returns {Promise<{ rows: Array<[string, number]>, pages: number }>} The rows and the total number of pages
 */
async function fetchSkuIndexPage(page) {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
  const url = new URL(root, window.location.origin);

  url.searchParams.set('section_id', SKU_INDEX_SECTION_ID);
  url.searchParams.set('page', String(page));

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`SKU list page ${page} failed (${response.status})`);

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const dataElement = doc.querySelector('script[data-mr-sku-index]');
  if (!dataElement || !dataElement.textContent) throw new Error(`SKU list page ${page} had no data`);

  const rows = JSON.parse(dataElement.textContent);
  if (!Array.isArray(rows)) throw new Error(`SKU list page ${page} was not a list`);

  return { rows, pages: Number(dataElement.getAttribute('data-pages')) || 1 };
}

/**
 * Loads the full SKU list - from the browser's storage if fresh, otherwise by downloading it.
 * @returns {Promise<SkuRow[]>} The searchable rows
 */
async function loadSkuRows() {
  let rows = readStoredSkuIndex();

  if (!rows) {
    const first = await fetchSkuIndexPage(1);
    const downloaded = first.rows.slice();
    const pending = [];
    let failed = false;

    for (let page = 2; page <= first.pages; page++) {
      pending.push(page);
    }

    const worker = async () => {
      while (pending.length > 0) {
        const page = pending.shift();
        if (page === undefined) return;

        try {
          const result = await fetchSkuIndexPage(page);
          downloaded.push(...result.rows);
        } catch (error) {
          failed = true;
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(SKU_INDEX_PARALLEL_REQUESTS, pending.length) }, worker));

    // Only keep a complete list - a partial one would be stored for 24 hours.
    if (!failed) storeSkuIndex(downloaded);

    rows = downloaded;
  }

  return rows.map(([sku, productId]) => ({ normalised: normaliseSku(sku), productId: Number(productId) }));
}

/**
 * Gets the searchable SKU rows, starting the download if it has not started yet.
 * Never rejects: if anything fails this resolves to an empty list and tries again next time.
 * @returns {Promise<SkuRow[]>} The searchable rows
 */
function getSkuRows() {
  if (skuRowsPromise) return skuRowsPromise;

  skuRowsPromise = loadSkuRows().catch(() => {
    skuRowsPromise = null;
    return /** @type {SkuRow[]} */ ([]);
  });

  return skuRowsPromise;
}

/**
 * Finds the products whose SKU matches what was typed.
 * @param {SkuRow[]} rows - The searchable rows
 * @param {string} term - What was typed
 * @returns {number[]} Product ids, best match first
 */
function findSkuMatchProductIds(rows, term) {
  const query = normaliseSku(term);
  if (query.length < SKU_MIN_TERM_LENGTH) return [];

  /** @type {Map<number, number>} */
  const bestScore = new Map();
  /** @type {number[]} */
  const firstSeen = [];

  for (const row of rows) {
    if (!row.normalised.includes(query)) continue;

    // 0 = exact, 1 = starts with, 2 = contains somewhere in the middle or end
    const rank = row.normalised === query ? 0 : row.normalised.startsWith(query) ? 1 : 2;
    if (rank === 2 && query.length < SKU_MIN_CONTAINS_LENGTH) continue;

    // Within the same rank, a shorter SKU is the closer match.
    const score = rank * 1000 + row.normalised.length;
    const existing = bestScore.get(row.productId);

    if (existing === undefined) {
      bestScore.set(row.productId, score);
      firstSeen.push(row.productId);
    } else if (score < existing) {
      bestScore.set(row.productId, score);
    }
  }

  return firstSeen
    .map((productId, position) => ({ productId, position, score: bestScore.get(productId) ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => a.score - b.score || a.position - b.position)
    .slice(0, SKU_MAX_RESULTS)
    .map((entry) => entry.productId);
}

/**
 * Gets the grid cards for the given products, drawn by the results section itself.
 * @param {number[]} productIds - The products to get cards for
 * @param {string} sectionId - The id of the search results section
 * @returns {Promise<Map<string, Element>>} The cards, keyed by product id
 */
async function fetchSkuMatchCards(productIds, sectionId) {
  const url = new URL(Theme.routes.search_url, window.location.origin);

  url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('section_id', sectionId);

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`SKU match cards failed (${response.status})`);

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  /** @type {Map<string, Element>} */
  const cards = new Map();

  for (const item of Array.from(doc.querySelectorAll('.product-grid__item'))) {
    const productId = item.getAttribute('data-product-id');
    if (productId) cards.set(productId, item);
  }

  return cards;
}

/**
 * Puts the SKU match cards at the top of the grid.
 * @param {Element} grid - The product grid (ul)
 * @param {number[]} productIds - The matching products, best first
 * @param {Map<string, Element>} cards - The cards, keyed by product id
 * @param {boolean} replaceExisting - True when Shopify found nothing and the grid only holds fallback products
 * @param {string} term - What was searched for
 */
function showSkuMatches(grid, productIds, cards, replaceExisting, term) {
  const gridItems = () => Array.from(grid.querySelectorAll(':scope > .product-grid__item'));

  if (replaceExisting) {
    for (const item of gridItems()) item.remove();

    const title = grid.parentElement ? grid.parentElement.querySelector('.main-collection-grid__title') : null;
    if (title) title.remove();
  }

  /** @type {Element | null} */
  let lastInserted = null;
  let inserted = 0;

  for (const productId of productIds) {
    const card = cards.get(String(productId));
    if (!card) continue;

    // The product may also be further down Shopify's own results - show it once, at the top.
    for (const item of gridItems()) {
      if (item.getAttribute('data-product-id') === String(productId)) item.remove();
    }

    const node = /** @type {Element} */ (document.importNode(card, true));
    node.setAttribute('data-mr-sku-match', '');

    if (lastInserted) {
      lastInserted.after(node);
    } else {
      grid.prepend(node);
    }

    lastInserted = node;
    inserted += 1;
  }

  if (inserted > 0) {
    const note = document.createElement('p');
    note.className = 'mr-sku-results__note';
    note.textContent =
      (inserted === 1 ? '1 product has' : inserted + ' products have') +
      ' a SKU matching \u201c' +
      term +
      '\u201d and ' +
      (inserted === 1 ? 'is' : 'are') +
      ' shown first.';
    grid.before(note);
  }
}

async function run() {
  const marker = document.querySelector('[data-mr-sku-results]');
  if (!marker) return;

  const term = marker.getAttribute('data-term') || '';
  const sectionId = marker.getAttribute('data-section-id') || '';
  const replaceExisting = marker.getAttribute('data-fallback') === 'true';
  if (!term || !sectionId) return;

  const resultsList = marker.closest('results-list');
  const grid = resultsList ? resultsList.querySelector('ul.product-grid') : null;
  if (!grid) return;

  const rows = await getSkuRows();
  const productIds = findSkuMatchProductIds(rows, term);
  if (productIds.length === 0) return;

  const cards = await fetchSkuMatchCards(productIds, sectionId);
  if (cards.size === 0) return;

  // The grid may have been re-drawn (sorted or filtered) while this was loading.
  if (!document.body.contains(marker) || !document.body.contains(grid)) return;

  showSkuMatches(grid, productIds, cards, replaceExisting, term);
}

run().catch(() => {
  // Fail safe: leave the page exactly as Shopify rendered it.
});
